import type { DynamicEntitiesFetcher } from '../../DynamicEntities/core/api/fetcher';
import { resolveFetcher } from '../../DynamicEntities/core/api/fetcher';

export interface BulkUpdatePayload {
  recordIds: string[];
  entityPatches: Array<{
    entityCode: string;
    fieldValues: Record<string, unknown>;
  }>;
}

export interface BulkProgressEvent {
  processed: number;
  total: number;
  succeeded: number;
  failed: number;
  isComplete: boolean;
  errors?: Array<{ recordId: string; reason: string }>;
}

export class BulkDynamicEditService {
  private baseUrl: string;
  private http: DynamicEntitiesFetcher;

  /**
   * @param baseUrl Raíz de la API.
   * @param fetcher Transporte del anfitrión (con `Authorization` y demás
   * cabeceras). Ausente → `fetch` global, igual que antes.
   */
  constructor(baseUrl: string, fetcher?: DynamicEntitiesFetcher) {
    this.baseUrl = baseUrl;
    this.http = resolveFetcher(fetcher);
  }

  async bulkUpdate(
    featureCode: string,
    payload: BulkUpdatePayload,
    onProgress: (event: BulkProgressEvent) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const url = `${this.baseUrl}/dynamic-entities/${featureCode}/bulk-update`;

    const response = await this.http(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(payload),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Error al iniciar la operación: ${response.status} ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No se pudo obtener el stream de respuesta');

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let eventType = '';
        let dataLine = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataLine = line.slice(5).trim();
          } else if (line === '' && dataLine) {
            try {
              const parsed: BulkProgressEvent = JSON.parse(dataLine);
              onProgress(parsed);
            } catch {
              // linea SSE malformada, ignorar
            }
            eventType = '';
            dataLine = '';
          }
        }

        // Si llega data sin event separador (solo data:)
        if (dataLine) {
          try {
            const parsed: BulkProgressEvent = JSON.parse(dataLine);
            onProgress(parsed);
          } catch {
            // ignorar
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}
